import React, { useMemo } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Link } from 'react-router-dom';
import { useSetRecoilState, useRecoilValue } from 'recoil';

import { PrimaryTable, PrimaryTableRow } from '@Calix-Commerce/design-system/components';

import { useFormatPrice } from 'utils/hooks';
import { formatDate } from 'utils/helpers/date';
import { getAccountsUrlWithReturn } from 'utils/helpers/url';
import { loadingState, authenticationState } from 'state/atoms';
import { VAR } from 'connectors/userTypes';

import { TableRowDotted, TableStatus } from './styledComponents';

const ResultsTable = ({ orderData, loading, error, hasSearched }) => {
  const { userType, account, accountId, endCustomerAccountId } =
    useRecoilValue(authenticationState);
  const setLoading = useSetRecoilState(loadingState);

  const resultsData = useMemo(() => {
    const results = orderData && orderData.getAccount && orderData.getAccount.searchOrders;

    return (
      results &&
      results.slice().sort((a, b) => new Date(b.fiscalOrderDate) - new Date(a.fiscalOrderDate))
    );
  }, [orderData]);

  const hasResults = resultsData && resultsData.length;
  const formatPrice = useFormatPrice();

  const renderTableMessage = () => {
    if (hasResults || loading) {
      return null;
    } else if (!hasSearched) {
      return <TableStatus>Please, select a search method and submit your search.</TableStatus>;
    } else if (error) {
      return (
        <TableStatus>
          {error.networkError
            ? 'Your search has timed out. Please refine your search criteria and try again.'
            : 'Error: Something went wrong'}
        </TableStatus>
      );
    } else if (resultsData && resultsData.length === 0) {
      const isVarOwnAccount = userType === VAR && endCustomerAccountId === accountId.substr(0, 15);

      return (
        <TableStatus>
          No orders found. Please adjust your search and try again.
          {isVarOwnAccount && (
            <p style={{ fontStyle: 'italic' }}>
              You are currently searching for orders for <b>{account}</b>. If you are looking for an
              order you've placed for a different account, please return to the{' '}
              <a href={getAccountsUrlWithReturn()}>account search</a> to select the account in
              question.
            </p>
          )}
        </TableStatus>
      );
    }
  };

  const renderResults = () => {
    return resultsData.map((result) => (
      <React.Fragment key={result.salesOrderNumber}>
        <TableRowDotted>
          <td>
            <div>
              <Link
                to={{ pathname: `/orders/${result.salesOrderNumber}` }}
                style={{ textDecoration: 'none' }}
                onClick={() => {
                  setLoading(true);
                }}
              >
                {result.salesOrderNumber}
              </Link>
            </div>
          </td>
          <td>
            <div>{result.purchaseOrderNumber}</div>
          </td>
          <td>
            <div>{result.orderedBy}</div>
          </td>
          <td>
            <div>{result.orderStatus}</div>
          </td>
          <td>
            <div>{formatPrice(result.orderTotal, result.currencyCode)}</div>
          </td>
        </TableRowDotted>
        <PrimaryTableRow>
          <td colSpan="3">
            <div>Order Date: {formatDate(result.fiscalOrderDate)}</div>
            <div>Order Type: {result.orderType}</div>
          </td>
        </PrimaryTableRow>
      </React.Fragment>
    ));
  };

  return (
    <>
      <PrimaryTable>
        <thead>
          <th>SO #</th>
          <th>PO #</th>
          <th>Ordered By</th>
          <th>Status</th>
          <th>Total</th>
        </thead>
        <tbody>{resultsData && renderResults()}</tbody>
      </PrimaryTable>
      {loading && (
        <TableStatus>
          <CircularProgress />
        </TableStatus>
      )}
      {renderTableMessage()}
    </>
  );
};

export { ResultsTable };
